import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './index.css';
import './i18n'; // Importar i18n para inicializar traducciones
import { StatusBar, Style } from '@capacitor/status-bar';
import { Capacitor } from '@capacitor/core';
import { AuthProvider } from './contexts/AuthContext';

import { OfflineProvider } from './contexts/OfflineContext';
import { ToastProvider } from './contexts/ToastContext';
import { ConfirmProvider } from './contexts/ConfirmContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ViewModeProvider } from './contexts/ViewModeContext';
import { BrowserRouter, HashRouter } from 'react-router-dom';
import GlobalErrorBoundary from './components/GlobalErrorBoundary';
import { logger } from './utils/logger';
import { PerformanceMonitor } from './utils/performance';
import { QueryProvider } from './providers/QueryProvider';
import { telemetry } from './utils/telemetry';

// Inicializar telemetría (PostHog / Analíticas)
telemetry.init();

// Monitoreo de inicio de aplicación
PerformanceMonitor.startTimer('app_initialization')

// El App ya no necesita envolver en QueryProvider porque se hará en el root
const AppWithProviders = () => (
  <App />
)

// Initialize Sentry AFTER page load + idle (keeps it outside Lighthouse measurement window)
if (import.meta.env.VITE_SENTRY_DSN) {
  const loadSentry = () => {
    // replayIntegration removed: saves ~41 KiB, not needed for error tracking
    import('@sentry/react').then((Sentry) => {
      Sentry.init({
        dsn: import.meta.env.VITE_SENTRY_DSN,
        integrations: [
          Sentry.browserTracingIntegration(),
        ],
        tracesSampleRate: 0.2,
        environment: import.meta.env.MODE,
      });
    }).catch(err => console.error('Failed to load Sentry', err));
  };
  // Load only after window is fully loaded AND browser is idle (best-effort)
  window.addEventListener('load', () => {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(loadSentry, { timeout: 10000 });
    } else {
      setTimeout(loadSentry, 8000);
    }
  });
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Could not find root element to mount to");
}


const root = ReactDOM.createRoot(rootElement);
root.render(
  <React.StrictMode>
    <QueryProvider>
      <GlobalErrorBoundary>
        <ThemeProvider>
          <ViewModeProvider>
            <ToastProvider>
              <ConfirmProvider>
                <OfflineProvider>
                  <AuthProvider>
                      {Capacitor.isNativePlatform() ? (
                        <HashRouter>
                          <AppWithProviders />
                        </HashRouter>
                      ) : (
                        <BrowserRouter>
                          <AppWithProviders />
                        </BrowserRouter>
                      )}
                  </AuthProvider>
                </OfflineProvider>
              </ConfirmProvider>
            </ToastProvider>
          </ViewModeProvider>
        </ThemeProvider>
      </GlobalErrorBoundary>
    </QueryProvider>
  </React.StrictMode>
);

// Dismiss the inline app shell with a smooth fade once React is mounted
const shell = document.getElementById('app-shell');
if (shell) {
  shell.classList.add('hidden');
  setTimeout(() => shell.remove(), 250);
}

// V2300: Hide native splash screen once React is mounted (prevents black screen flash)
if (Capacitor.isNativePlatform()) {
  import('@capacitor/splash-screen').then(({ SplashScreen }) => {
    SplashScreen.hide().catch(err => logger.error('Error hiding splash screen', err));
  });
}

// Log de inicialización completada
const initDuration = PerformanceMonitor.endTimer('app_initialization', {
  platform: Capacitor.getPlatform(),
  environment: import.meta.env.MODE,
  isNative: Capacitor.isNativePlatform()
})

logger.info('Application initialized successfully', {
  initDuration: Math.round(initDuration),
  platform: Capacitor.getPlatform(),
  environment: import.meta.env.MODE
})

// Register Service Worker for PWA (Production Polish)
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js', { scope: '/' })
      .then((registration) => {
        logger.info('Service Worker registered successfully', { scope: registration.scope });
        
        // Check for updates
        registration.onupdatefound = () => {
          const installingWorker = registration.installing;
          if (installingWorker) {
            installingWorker.onstatechange = () => {
              if (installingWorker.state === 'installed') {
                if (navigator.serviceWorker.controller) {
                  logger.info('New content is available; please refresh.');
                } else {
                  logger.info('Content is cached for offline use.');
                }
              }
            };
          }
        };
      })
      .catch((error) => {
        logger.error('Service Worker registration failed', { error });
      });
  });
}

// Configure status bar for mobile app - respetar tema guardado o del sistema (v0.4.8)
if (Capacitor.isNativePlatform()) {
  const initStatusBar = async () => {
    try {
      const saved = localStorage.getItem('theme');
      let isDark = saved === 'dark';
      
      if (!saved) {
        isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      }
      
      await StatusBar.setStyle({ style: isDark ? Style.Dark : Style.Light });
      await StatusBar.setBackgroundColor({ color: isDark ? '#101622' : '#ffffff' });
    } catch (e) {
      console.warn('StatusBar configuration failed', e);
    }
  };
  initStatusBar();
}